import { useMemo } from 'react';
import { ThreadItem } from '@weave/schema-gen-ts/dist/schemas/sms/shared/v2/models.pb';
import { QueryOptions } from 'react-query';
import { useGetFusionEnabled } from '../utils';
import { isCallRecordItem, isSmsMessageItem, isAiActivityItem } from '../utils/thread-items-utils';
import { useGetUnifiedThreadQuery as useGetUnifiedThreadV4Query } from '../v4/queries';

type UseGetThreadQueryDataProps = {
  threadId: string;
  locationId: string;
  focusItemId?: string;
  focusItemCreatedAt?: string;
  enabled?: boolean;
  retryLimit?: QueryOptions['retry'];
  cacheTime?: number;
};

export const useGetThreadQueryData = ({
  threadId,
  locationId,
  focusItemId,
  focusItemCreatedAt,
  enabled,
  retryLimit,
  cacheTime,
}: UseGetThreadQueryDataProps) => {
  const fusionEnabled = useGetFusionEnabled([locationId]);

  const v4Request = useMemo(
    () => ({
      threadId,
      locationId,
      pageSize: 50,
      filter: {
        focusThreadItem: focusItemId,
        focusCreatedAt: focusItemCreatedAt,
        showDeleted: true,
      },
    }),
    [threadId, locationId, focusItemId, focusItemCreatedAt]
  );

  const getThreadQuery = useGetUnifiedThreadV4Query({
    request: v4Request,
    options: {
      retry: retryLimit !== undefined ? retryLimit : 1,
      enabled: enabled !== undefined ? enabled : true,
      ...(cacheTime !== undefined && { cacheTime }),
    },
  });

  const threadItems: ThreadItem[] = useMemo(() => {
    return (
      getThreadQuery.data?.pages
        .flatMap((page) =>
          (page.thread.items ?? []).filter(
            (item) =>
              isSmsMessageItem(item) ||
              (isCallRecordItem(item) && !!item.callRecord) ||
              (fusionEnabled && isAiActivityItem(item) && !!item.aiActivity)
          )
        )
        .reverse() ?? []
    );
  }, [fusionEnabled, getThreadQuery.data]);

  return {
    getThreadQuery,
    threadItems,
  };
};
